import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import { DatePicker, Button, Table } from "antd";
import dayjs from "dayjs";
import axios from "../../utils/axiosDash";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────
const C = {
  red:         "#E24B4A",
  redLight:    "#FCEBEB",
  amber:       "#BA7517",
  amberLight:  "#FAEEDA",
  amberMid:    "#FAC775",
  green:       "#3B6D11",
  greenLight:  "#EAF3DE",
  greenMid:    "#97C459",
  blue:        "#185FA5",
  blueLight:   "#E6F1FB",
  blueMid:     "#378ADD",
  teal:        "#0F6E56",
  tealLight:   "#E1F5EE",
  purple:      "#534AB7",
  gray:        "#888780",
  grayLight:   "#F1EFE8",
  dark:        "#2C2C2A",
  border:      "rgba(0,0,0,0.08)",
};

const ZONE_META = {
  Intrusion:    { color: C.red,    bg: C.redLight,   label: "Intrusion"   },
  Tailgating:   { color: C.amber,  bg: C.amberLight, label: "Tailgating"  },
  "Head Count": { color: C.blue,   bg: C.blueLight,  label: "Head Count"  },
};

const PALETTE = [C.blueMid, C.red, C.amberMid, C.greenMid, C.purple, C.teal];

// ── DATA PROCESSING ────────────────────────────────────────────────────────
function processData(rawData) {
  if (!rawData.length) return {
    aggregatedByCam: [],
    alertTimeSeries: [],
    headcountSeries: [],
    recentEvents: [],
    zoneTypeData: [],
    camIds: [],
    total: 0,
    intrusions: 0,
    tailgating: 0,
    headcounts: 0,
  };

  const rows = rawData.map(item => ({
    ...item,
    _time: item.timestamp || item.createdAt?.$date || new Date().toISOString(),
    _persons: item.zoneType === "Head Count" ? (item.details?.total_persons || 0) : 0,
  }));

  const sorted = [...rows].sort((a, b) => new Date(a._time) - new Date(b._time));

  // Cam totals
  const camTotals = {};
  rows.forEach(r => {
    if (!camTotals[r.cameraName]) {
      camTotals[r.cameraName] = {
        cam: r.cameraName,
        location: r.location,
        module: r.zoneType,
        count: 0,
      };
    }
    camTotals[r.cameraName].count++;
  });
  const aggregatedByCam = Object.values(camTotals).sort((a, b) => b.count - a.count);

  // Hourly buckets by zone type
  const bucketMap = {};
  sorted.forEach(r => {
    const h = dayjs(r._time).format("HH:00");
    if (!bucketMap[h]) bucketMap[h] = { time: h, Intrusion: 0, Tailgating: 0, "Head Count": 0 };
    if (bucketMap[h][r.zoneType] !== undefined) bucketMap[h][r.zoneType]++;
  });
  const alertTimeSeries = Object.values(bucketMap).slice(-24);

  // Headcount series per camera
  const hcMap = {};
  sorted.forEach(r => {
    if (r.zoneType === "Head Count") {
      const h = dayjs(r._time).format("HH:00");
      if (!hcMap[h]) hcMap[h] = { time: h };
      hcMap[h][r.cameraName] = r._persons;
    }
  });
  const headcountSeries = Object.values(hcMap).slice(-24);

  // Recent events (newest first, all of them)
  const recentEvents = [...rows].sort((a, b) => new Date(b._time) - new Date(a._time));

  // Zone type distribution
  const zoneTypeData = Object.keys(ZONE_META).map(z => ({
    name: z,
    value: rows.filter(r => r.zoneType === z).length,
    color: ZONE_META[z].color,
  })).filter(d => d.value > 0);

  return {
    aggregatedByCam,
    alertTimeSeries,
    headcountSeries,
    recentEvents,
    zoneTypeData,
    camIds: aggregatedByCam.map(c => c.cam),
    total: rows.length,
    intrusions:  rows.filter(r => r.zoneType === "Intrusion").length,
    tailgating:  rows.filter(r => r.zoneType === "Tailgating").length,
    headcounts:  rows.filter(r => r.zoneType === "Head Count").length,
  };
}

// ── CUSTOM TOOLTIP ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, unit = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
      <p style={{ fontWeight: 700, color: C.dark, marginBottom: 6, fontSize: 11 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, margin: "2px 0", fontWeight: 600 }}>
          {p.name}: <span style={{ color: C.dark }}>{p.value}{unit}</span>
        </p>
      ))}
    </div>
  );
};

// ── KPI CARD ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color, bg }) => (
  <div style={{
    background: bg || "#fff",
    border: `1px solid ${color}22`,
    borderRadius: 16,
    padding: "20px 24px",
    position: "relative",
    overflow: "hidden",
    flex: 1,
    minWidth: 160,
  }}>
    <div style={{ position: "absolute", top: 0, right: 0, width: 72, height: 72, background: `${color}12`, borderRadius: "0 16px 0 72px" }} />
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: C.gray, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 38, fontWeight: 800, color, lineHeight: 1, marginBottom: 6 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.gray }}>{sub}</div>}
  </div>
);

// ── ZONE PILL ──────────────────────────────────────────────────────────────
const ZonePill = ({ zone }) => {
  const m = ZONE_META[zone] || { color: C.gray, bg: C.grayLight, label: zone };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
};

// ── FEED ROW ───────────────────────────────────────────────────────────────
const FeedRow = ({ event }) => {
  const zm = ZONE_META[event.zoneType] || {};
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 16px", borderBottom: `1px solid ${C.border}`, transition: "background 0.15s", cursor: "default" }}
      onMouseEnter={e => e.currentTarget.style.background = C.grayLight}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {event.imageUrl ? (
        <img src={event.imageUrl} alt="" style={{ width: 52, height: 38, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 52, height: 38, borderRadius: 6, background: zm.bg || C.grayLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
          📷
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: C.dark }}>{event.cameraName}</span>
          <ZonePill zone={event.zoneType} />
        </div>
        <div style={{ fontSize: 11, color: C.gray, marginTop: 3 }}>
          {event.location} · {dayjs(event._time).format("MMM D, HH:mm:ss")}
        </div>
        {event.details?.total_persons > 0 && (
          <div style={{ fontSize: 11, color: C.blue, marginTop: 2, fontWeight: 600 }}>
            {event.details.total_persons} persons detected
            {event.details.kitchen_persons != null && ` · Kitchen: ${event.details.kitchen_persons}`}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: C.gray, flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontWeight: 600, color: C.dark }}>#{event.eventId}</div>
        <div style={{ marginTop: 2, background: event.cameraStatus === "Online" ? C.greenLight : C.redLight, color: event.cameraStatus === "Online" ? C.green : C.red, padding: "1px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
          {event.cameraStatus || "—"}
        </div>
      </div>
    </div>
  );
};

// ── PANEL ──────────────────────────────────────────────────────────────────
const Panel = ({ children, style = {} }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 16, padding: "20px", ...style }}>
    {children}
  </div>
);

const SectionHead = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 800, fontSize: 14, color: C.dark }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: C.gray, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── MAIN ───────────────────────────────────────────────────────────────────
export default function Reports() {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [feedFilter, setFeedFilter] = useState("All");

  const d = useMemo(() => processData(rawData), [rawData]);

  const fetchData = async (date) => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-for-alerts-reports-tablespace", {
        filterDate: (date || selectedDate).format("YYYY-MM-DD"),
      });
      if (res.data.success) setRawData(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedDate); }, [selectedDate]);

  const axisProps = { tick: { fontSize: 10, fill: C.gray }, tickLine: false, axisLine: false };

  // Filtered feed
  const filteredFeed = feedFilter === "All"
    ? d.recentEvents
    : d.recentEvents.filter(e => e.zoneType === feedFilter);

  // Event log table
  const eventColumns = [
    {
      title: "Camera", dataIndex: "cam", key: "cam",
      render: v => <span style={{ fontWeight: 700, color: C.dark, fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Location", dataIndex: "location", key: "location",
      render: v => <span style={{ color: C.gray, fontSize: 12 }}>{v}</span>,
    },
    {
      title: "Zone Type", dataIndex: "module", key: "module",
      render: v => <ZonePill zone={v} />,
    },
    {
      title: "Total Events", dataIndex: "count", key: "count",
      render: v => <span style={{ fontWeight: 800, fontSize: 15, color: C.dark }}>{v}</span>,
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: "descend",
    },
  ];

  const TABS = [
    { key: "overview",  label: "Overview" },
    { key: "cameras",   label: "Camera Analytics" },
    { key: "feed",      label: "Alerts" },
    { key: "events",    label: "Event Log" },
  ];

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: 1560, margin: "0 auto" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, boxShadow: `0 0 0 3px ${C.greenLight}` }} />
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: C.teal }}>Live Analytics</span>
            </div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: C.dark, letterSpacing: "-0.5px" }}>Security Reports</h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.gray }}>Real-time data across all surveillance zones</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <DatePicker
              value={selectedDate}
              onChange={val => setSelectedDate(val || dayjs())}
              format="MMM D, YYYY"
              allowClear={false}
              style={{ borderRadius: 40, height: 40 }}
            />
            <Button
              onClick={() => fetchData(selectedDate)}
              loading={loading}
              style={{ borderRadius: 40, height: 40, fontWeight: 700, background: C.dark, color: "#fff", border: "none", fontFamily: "inherit" }}
            >
              ↻ Refresh
            </Button>
          </div>
        </div>

        {/* KPI STRIP */}
        <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <KpiCard label="Total Events"  value={d.total}      sub="All detections"           color={C.dark}   bg="#fff"         />
          <KpiCard label="Intrusions"    value={d.intrusions} sub="Unauthorised zone access"  color={C.red}    bg={C.redLight}   />
          <KpiCard label="Tailgating"    value={d.tailgating} sub="Piggybacking detections"   color={C.amber}  bg={C.amberLight} />
          <KpiCard label="Head Count"    value={d.headcounts} sub="Occupancy events"           color={C.blue}   bg={C.blueLight}  />
          <KpiCard label="Cameras"       value={d.camIds.length} sub="Active in period"        color={C.teal}   bg={C.tealLight}  />
        </div>

        {/* TABS CONTAINER */}
        <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 20, overflow: "hidden" }}>
          <div style={{ borderBottom: `1px solid ${C.border}`, padding: "0 24px" }}>
            <div style={{ display: "flex" }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: "16px 20px", border: "none",
                    borderBottom: activeTab === t.key ? `2px solid ${C.dark}` : "2px solid transparent",
                    background: "none",
                    fontWeight: activeTab === t.key ? 800 : 500,
                    fontSize: 13,
                    color: activeTab === t.key ? C.dark : C.gray,
                    cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
                  }}
                >{t.label}</button>
              ))}
            </div>
          </div>

          <div style={{ padding: "24px" }}>

            {/* ── OVERVIEW ── */}
            {activeTab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}>

                  {/* Hourly Alert Volume */}
                  <Panel>
                    <SectionHead title="Event Volume (Hourly)" sub="Detections grouped by zone type" />
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={d.alertTimeSeries} barCategoryGap="35%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="Intrusion"   name="Intrusion"   stackId="a" fill={C.red}     radius={[0,0,0,0]} />
                        <Bar dataKey="Tailgating"  name="Tailgating"  stackId="a" fill={C.amberMid} radius={[0,0,0,0]} />
                        <Bar dataKey="Head Count"  name="Head Count"  stackId="a" fill={C.blueMid}  radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                      {[["Intrusion", C.red], ["Tailgating", C.amberMid], ["Head Count", C.blueMid]].map(([l, c]) => (
                        <span key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.gray }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }} />{l}
                        </span>
                      ))}
                    </div>
                  </Panel>

                  {/* Zone Distribution Donut */}
                  <Panel>
                    <SectionHead title="By Zone Type" sub="Distribution of all detections" />
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart>
                        <Pie
                          data={d.zoneTypeData} cx="50%" cy="50%"
                          innerRadius={50} outerRadius={78} paddingAngle={3}
                          dataKey="value"
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {d.zoneTypeData.map((e, i) => <Cell key={i} fill={e.color} stroke="white" strokeWidth={2} />)}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
                      {d.zoneTypeData.map(z => (
                        <div key={z.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: z.color, flexShrink: 0, display: "inline-block" }} />
                            <span style={{ color: C.dark, fontWeight: 600 }}>{z.name}</span>
                          </span>
                          <span style={{ fontWeight: 800, fontSize: 14, color: z.color }}>{z.value}</span>
                        </div>
                      ))}
                      {d.zoneTypeData.length === 0 && (
                        <div style={{ textAlign: "center", color: C.gray, padding: "16px 0", fontSize: 12 }}>No data</div>
                      )}
                    </div>
                  </Panel>
                </div>

                {/* Headcount trend */}
                {d.headcountSeries.length > 0 && (
                  <Panel>
                    <SectionHead title="Occupancy Trend" sub="Head count per camera over time" />
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={d.headcountSeries}>
                        <defs>
                          {Object.keys(d.headcountSeries[0] || {}).filter(k => k !== "time").map((cam, i) => (
                            <linearGradient key={cam} id={`hcg${i}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0.18} />
                              <stop offset="95%" stopColor={PALETTE[i % PALETTE.length]} stopOpacity={0} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" vertical={false} />
                        <XAxis dataKey="time" {...axisProps} />
                        <YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip content={<ChartTooltip unit=" persons" />} />
                        {Object.keys(d.headcountSeries[0] || {}).filter(k => k !== "time").map((cam, i) => (
                          <Area key={cam} type="monotone" dataKey={cam} name={cam}
                            stroke={PALETTE[i % PALETTE.length]} strokeWidth={2}
                            fill={`url(#hcg${i})`} dot={false} />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Panel>
                )}
              </div>
            )}

            {/* ── CAMERA ANALYTICS ── */}
            {activeTab === "cameras" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                <Panel>
                  <SectionHead title="Events by Camera" sub="Total detections per surveillance point" />
                  <ResponsiveContainer width="100%" height={Math.max(d.aggregatedByCam.length * 52, 240)}>
                    <BarChart data={d.aggregatedByCam} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0eeea" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="cam" tick={{ fontSize: 12, fontWeight: 600, fill: C.dark }} width={140} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="count" name="Total Events" radius={[0, 8, 8, 0]}>
                        {d.aggregatedByCam.map((e, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel>
                  <SectionHead title="Camera Rankings" sub="By total event count" />
                  {d.aggregatedByCam.length === 0 && (
                    <div style={{ textAlign: "center", color: C.gray, padding: "40px 0", fontSize: 13 }}>No data for selected date</div>
                  )}
                  {d.aggregatedByCam.map((cam, i) => {
                    const max = Math.max(...d.aggregatedByCam.map(c => c.count), 1);
                    const pct = (cam.count / max) * 100;
                    const barColor = PALETTE[i % PALETTE.length];
                    return (
                      <div key={cam.cam} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: i < d.aggregatedByCam.length - 1 ? `1px solid ${C.border}` : "none" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ width: 24, height: 24, borderRadius: "50%", background: `${barColor}18`, color: barColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                              #{i + 1}
                            </span>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: 13, color: C.dark }}>{cam.cam}</span>
                              <span style={{ fontSize: 11, color: C.gray, marginLeft: 8 }}>{cam.location}</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <ZonePill zone={cam.module} />
                            <span style={{ fontWeight: 800, fontSize: 16, color: C.dark }}>{cam.count}</span>
                          </div>
                        </div>
                        <div style={{ height: 6, background: C.grayLight, borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: 3, transition: "width 0.6s ease" }} />
                        </div>
                      </div>
                    );
                  })}
                </Panel>
              </div>
            )}

            {/* ── ALERTS FEED ── */}
            {activeTab === "feed" && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                  <SectionHead title="All Alerts" sub={`${filteredFeed.length} events · ${selectedDate.format("MMM D, YYYY")}`} />
                  <div style={{ display: "flex", gap: 6 }}>
                    {["All", "Intrusion", "Tailgating", "Head Count"].map(f => {
                      const active = feedFilter === f;
                      const m = ZONE_META[f] || {};
                      return (
                        <button key={f} onClick={() => setFeedFilter(f)} style={{
                          padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "none",
                          background: active ? (m.color || C.dark) : (m.bg || C.grayLight),
                          color: active ? "#fff" : (m.color || C.dark),
                          transition: "all 0.15s", fontFamily: "inherit",
                        }}>{f}</button>
                      );
                    })}
                  </div>
                </div>
                <Panel style={{ padding: 0, overflow: "hidden" }}>
                  {filteredFeed.length === 0 ? (
                    <div style={{ textAlign: "center", color: C.gray, padding: "48px 0", fontSize: 13 }}>No events for selected filter</div>
                  ) : (
                    filteredFeed.map((e, i) => <FeedRow key={e.uniqueEventId || i} event={e} />)
                  )}
                </Panel>
              </div>
            )}

            {/* ── EVENT LOG TABLE ── */}
            {activeTab === "events" && (
              <div>
                <SectionHead title="Event Log" sub="Aggregated summary per camera" />
                <Panel style={{ padding: 0, overflow: "hidden" }}>
                  <Table
                    dataSource={d.aggregatedByCam}
                    columns={eventColumns}
                    rowKey="cam"
                    pagination={{ pageSize: 10, size: "small" }}
                    size="middle"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  />
                </Panel>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}